import { Component, OnDestroy, OnInit } from '@angular/core'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { GlobalConstants } from '../../../constants/global-constants'
import { Observable, Subscription } from 'rxjs'
import { Store } from '@ngrx/store'
import { AppState } from '../../../store/state/app.state'
import { LOAD_ACTIVITY_SUMMARY_DETAILS } from '../../../store/action/activity.action'
import { ActivityAPIModel, PaginatedAPI } from '../../../models/api.model'
import { activitySummaryDetailsSelector } from '../../../store/selector/activity.selector'
import {
  ACTIVITY_ID,
  ACTIVITY_NAME,
  DAYS_ELAPSED,
  REQUEST_INITIATED_ON,
  RESPONSE_DUE_BY,
  TASK_CATEGORY
} from 'src/app/constants/ui.columns'
import { ServiceUrlConstants } from '../../../constants/service-url-constants'
import { DocumentService } from '../../../services/common/document.service'
import {
  getTaskCategorySubCategoryMapFromState,
  getUIStatusMappingFromState
} from '../../../store/selector/initialize.selector'
import { SAVE_SUMMARY_TABLE_SEARCH_PARAMS } from 'src/app/store/action/common/common.action'
import { ACTIVITY } from 'src/app/constants/field-id-constants'

@Component({
  selector: 'app-activity-summary',
  templateUrl: './activity-summary.component.html',
  styleUrls: ['./activity-summary.component.scss']
})
export class ActivitySummaryComponent implements OnInit, OnDestroy {
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<ActivityAPIModel>
  activitySummaryAPIData$: Observable<PaginatedAPI<ActivityAPIModel>>
  private uiStatusMapping: Map<string, string>
  private taskCategoryMap: Map<string, string>
  pageTitle = 'Activities Summary'
  dashboardPageLink = `/${GlobalConstants.ROUTING.ACTIVITY}/${GlobalConstants.ROUTING.DASHBOARD}`
  commonSubscription: Subscription
  readonly MODULE_NAME_ACTIVITY_TRACKER = GlobalConstants.MODULE.ACTIVITY_TRACKER

  constructor(private appState: Store<AppState>, private documentService: DocumentService) {
  }

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    mapReferenceDocuments: false,
    sortBy: 'createdDatetime',
    sortOrder: 'ASC'
  }

  searchRequestBody: any = {
    age: undefined
  }

  ngOnInit(): void {
    this.appState.select(getUIStatusMappingFromState).subscribe((uiStatusMapping) => {
      if (uiStatusMapping) {
        this.uiStatusMapping = uiStatusMapping
      }
    })
    this.appState
      .select(getTaskCategorySubCategoryMapFromState(this.MODULE_NAME_ACTIVITY_TRACKER))
      .subscribe(categorySubCategoryMap => {
        if (categorySubCategoryMap) {
          this.taskCategoryMap = new Map(Array.from(categorySubCategoryMap?.keys())?.map(category => [category, category]))
        }
      })

    this.activitySummaryAPIData$ = this.appState.select(activitySummaryDetailsSelector())

    this.summaryOptions = {
      downloadable: false,
      additionalButtons: true,
      contentSelectable: false,
      contentSelector: true,
      filterable: true,
      formattedContent: true,
      pageable: false,
      sortable: true
    }

    this.summaryData = {
      content$: this.activitySummaryAPIData$,
      contentSelector: {
        label: 'View for Days',
        selectedValue: this.searchRequestBody.age,
        selectionOptions: [
          {
            key: '7',
            value: '7 Days'
          },
          {
            key: '10',
            value: '10 Days'
          },
          {
            key: '15',
            value: '15 Days'
          },
          {
            key: '20',
            value: '20 Days'
          },
          {
            key: '25',
            value: '25 Days'
          },
          {
            key: undefined,
            value: 'ALL'
          }
        ],
        onChange: this.onViewForChange
      },
      cssClasses: 'rowFormatting',
      formattingLegend: [
        {
          formatLabel: 'High Priority',
          formatClassName: 'priority-high'
        },
        {
          formatLabel: 'Medium Priority',
          formatClassName: 'priority-medium'
        },
        {
          formatLabel: 'Low Priority',
          formatClassName: 'priority-low'
        }
      ],
      headers: [ACTIVITY_ID, ACTIVITY_NAME, REQUEST_INITIATED_ON, DAYS_ELAPSED, RESPONSE_DUE_BY],
      additionalButtons: [],
      sortCallback: this.sortCallback,
      rowFormatting: this.rowFormattingForDaysElapsed,
      currentSearchParams: this.queryParamValues,
      searchCallback: this.searchCallback,
      downloadDocumentCallback: this.downloadDocumentCallback,
      checkBoxSelectionIds: [],
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange
    }
    this.addFiltersWithDynamicOptions()

    this.commonSubscription = this.appState.select('common').subscribe((data) => {
      const activitySearchParams = data?.summaryTableData?.get(ACTIVITY)
      if (activitySearchParams) {
        const status = activitySearchParams?.searchRequestBody?.statusList?.[0]
        const parentStatus = this.uiStatusMapping?.[status]
        let searchRequestBody = activitySearchParams?.searchRequestBody
        if (parentStatus) {
          searchRequestBody = { ...searchRequestBody, statusList: parentStatus }
        }
        this.searchRequestBody = { ...this.searchRequestBody, age: activitySearchParams.searchRequestBody?.age }
        const contentSelector = {
          ...this.summaryData.contentSelector,
          selectedValue: activitySearchParams.searchRequestBody?.age
        }
        this.summaryData = { ...this.summaryData, appliedFilters: searchRequestBody, contentSelector }
        this.appState.dispatch(LOAD_ACTIVITY_SUMMARY_DETAILS(activitySearchParams))
      } else {
        this.loadSummaryData()
      }
    })
  }

  private loadSummaryData() {
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(LOAD_ACTIVITY_SUMMARY_DETAILS(options))
    this.appState.dispatch(SAVE_SUMMARY_TABLE_SEARCH_PARAMS({ owner: ACTIVITY, data: options }))
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  searchCallback = (searchParams) => {
    let selectedStatus = []
    if (searchParams.statusList) {
      let selected = searchParams.statusList
      for (let [key, value] of Object.entries(this.uiStatusMapping)) {
        if (selected === value) {
          selectedStatus.push(key)
        }
      }
    }
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some((value) => value !== undefined && value !== '')) {
      searchParams = {}
    }
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams,
      statusList: selectedStatus
    }
    this.loadSummaryData()
  }

  onViewForChange = ($event) => {
    if ($event.target.value != 'undefined') {
      const searchRequestBody = { ...this.searchRequestBody, age: $event.target.value }
      this.searchRequestBody = searchRequestBody
    } else {
      const searchRequestBody = { ...this.searchRequestBody, age: undefined }
      this.searchRequestBody = searchRequestBody
    }
    this.loadSummaryData()
  }

  downloadDocumentCallback = (documentType) => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_ACTIVITY_SUMMARY_LIST + ServiceUrlConstants.API_EXPORT + documentType}`

    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }

    this.documentService.downloadDocument(endPoint, options)
  }

  rowFormattingForDaysElapsed = (record: ActivityAPIModel): string => {
    if (record.daysElapsed > 7) {
      return 'priority-high'
    } else if (record.daysElapsed > 0 && record.daysElapsed <= 7) {
      return 'priority-medium'
    } else {
      return 'priority-low'
    }
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'mapReferenceDocuments',
        value: 'false'
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  downloadSelected = () => {
    console.log(this.summaryData.checkBoxSelectionIds)
  }

  selectPageCallback = (pageNum) => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  addFiltersWithDynamicOptions() {
    // Task Category
    TASK_CATEGORY.filter.getOptionsList = () => {
      return this.taskCategoryMap
    }
    this.summaryData?.headers?.splice(1, 0, TASK_CATEGORY)
  }

  ngOnDestroy(): void {
    this?.commonSubscription?.unsubscribe()
  }
}
